import type { AnswerItemDto } from "@9amhealth/openapi";
import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import clsx from "clsx";
import type { FC, ReactNode } from "react";
import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { getReleaseDateExclusions } from "src/ui/components/SignupCustomContent/campaigns/getReleaseDateExclusions";

import type { CachedObject } from "src/state/QuestionnaireCubit/QuestionnaireCubit";
import QuestionnaireCubit from "src/state/QuestionnaireCubit/QuestionnaireCubit";
import { QuestionnaireCubitIdContext } from "src/state/QuestionnaireCubit/QuestionnaireCubitIdProvider";
import type {
  CustomQuestionnaireAnswer,
  CustomQuestionnaireFilterAnswerOptions,
  CustomQuestionnaireResult,
  QuestionnaireState
} from "src/state/QuestionnaireCubit/QuestionnaireState";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import type { QuestionnaireField } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import QuestionnaireStep from "src/ui/components/QuestionnaireStep/QuestionnaireStep";
import QuestionnaireSavingState from "./QuestionnaireSavingState";
import { QuestionnaireStateRenderer } from "./QuestionnaireStateRenderer";
import { ProgressIndicator } from "@9amhealth/shared";
import Translate from "../Translate/Translate";

interface Props {
  /** Force repeat the questionnaire, this changes the initial state of some questionnaires */
  forceRepeat?: boolean;
  children?: ReactNode | undefined;
  id: string;
  answers?: AnswerItemDto[];
  hiddenFields?: CachedObject;
  useRouting?: boolean;
  disableTracking?: boolean;
  preview?: boolean;
  showThankYouStep?: boolean;
  autoSkip?: boolean;
  listAll?: boolean;
  formLike?: boolean;
  disableAutoSubmit?: boolean;
  preventLoadingUserAnswers?: boolean;
  instanceId?: string;
  resetScrollOnEachStep?: boolean;
  onLastStepCompleted?: () => void;
  onStep?: (step: number, total: number) => void;
  onDataSent?: (data: CustomQuestionnaireResult) => void;
  onQuestionnaireData?: (
    data: QuestionnaireState,
    bloc: QuestionnaireCubit
  ) => void;
  answerOverrides?: CustomQuestionnaireAnswer[];
  filterAnswerOptions?: CustomQuestionnaireFilterAnswerOptions[];
  onSavingChange?: (isSaving: boolean, error?: boolean) => void;
  onEndingScreenShown?: () => void;
  /** When true, the internal saving indicator UI is hidden (useful when parent handles saving UI) */
  hideSavingUI?: boolean;
}

export const WrapAllFields = styled.div`
  label: WrapAllFields;
  padding-bottom: 8em;

  > div {
    position: static !important;
  }

  nine-funnel-step {
    margin: 0 auto;
  }

  &.list-all {
    align-items: flex-start;
    display: flex;
    flex-direction: row;
    position: relative;
    min-width: calc(100vw - 4em);
    overflow-x: scroll;
    overscroll-behavior-x: contain;
    touch-action: none;
    scroll-snap-type: x mandatory;
    padding: 0 5vw;

    * {
      touch-action: none;
    }

    .form-step {
      display: flex;
      flex-direction: row;
      align-items: center;
      margin: 0;
      min-width: 90vw;
      box-shadow: var(--light-shadow);
      border-radius: var(--border-radius);

      scroll-snap-align: center;
      min-height: 100%;
    }

    nine-funnel-step {
      margin: 0 auto;
    }

    @media screen and (min-width: 768px) {
      min-width: calc(100vw - 4em - 520px);
    }
  }

  &.form-like {
    padding: 0;
    flex-direction: column;
    overflow: auto;
    width: auto;
    height: auto;
    background: var(--color-cream);

    .form-step {
      width: 100%;
      height: auto;
      min-height: auto;
      min-width: auto;
      box-shadow: none;
    }

    nine-funnel-step {
      margin: 0 0 0 1em;
      padding: 3em 1em 2em 1em;
      width: auto;
      position: relative;
      border-radius: 0;
      background: var(--bg, transparent);
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      transition: background 0.8s ease-in-out;
      z-index: 1;

      &:before {
        content: var(--text, "");
        position: absolute;
        transition: all 0.2s ease-in-out;
        border: 1px solid var(--color-cream);
        left: 1em;
        top: 1.5em;
        height: 1.8em;
        min-width: 2em;
        padding: 0 0.6em;
        display: flex;
        align-items: center;
        color: var(--text-color, var(--color-charcoal));

        font-size: 0.8rem;
        background: var(--status, var(--color-afternoon-green));
        border-radius: 0.6em;
      }

      margin-top: 0 !important;
      --funnel-step-padding: 0;
      --funnel-step-min-height: 0;
      --funnel-step-width: 100%;
      --funnel-step-text-align: left;

      .step-button,
      .step-other,
      nine-spacer {
        display: none;
      }

      .step-input {
        margin: 1.5em 0 0;
      }

      h4 {
        font-size: 1.4em;
        font-family: var(--font-family);
        font-weight: 500;
        text-align: left;
      }

      img {
        width: min(300px, 100%);
      }

      .scroll-to-me {
        opacity: 1;
        position: absolute;
        top: -4em;

        &:after,
        &:before {
          content: "";
          position: fixed;
          top: calc(var(--pos-in-document-percent) + 5px);
          left: 0;
          width: 0.9em;
          height: calc(var(--el-height-percent, 0));
          background: var(--color-afternoon-green);
          transition: all 0.2s ease-in-out;
          z-index: 1000;
        }

        &:before {
          width: 1em;
          background: transparent;
        }

        &:after {
          border: 1px solid var(--color-charcoal-60);
          /* border-radius: 6px; */
          width: 0.3em;
        }

        &:hover {
          &:after {
            width: 1em;
          }
        }
      }

      &.is-optional {
        --status: var(--color-sunrise-orange);
        --text: "Optional";

        .scroll-to-me {
          &:after {
            background: var(--color-sunrise-orange);
          }
        }
      }

      &.is-required.has-issue {
        --status: var(--color-error);
        --text: "Required";
        --text-color: white;

        .scroll-to-me {
          &:after {
            background: var(--color-error);
          }
        }
      }

      &.is-statement {
        --bg: var(--color-midmorning-pink);
        --text: "Statement";
        --status: var(--color-cream);
        border: none;
        border-radius: 0.6em;
        margin: 1em 1em 1em 2em !important;

        .step-input {
          display: none;
        }

        .scroll-to-me {
          &:after {
            background: var(--color-midmorning-pink);
          }
        }
      }

      &[data-el-active] {
        --bg: var(--color-sunrise-orange);
      }

      &:hover {
        z-index: 2;
      }
    }
  }
`;

const QuestionnaireContent: FC<Props> = ({
  useRouting,
  children,
  showThankYouStep,
  listAll,
  formLike,
  resetScrollOnEachStep,
  onStep,
  onQuestionnaireData,
  hideSavingUI
}) => {
  const [questionnaireState, questionnaireBloc] =
    useQuestionnaireCubitInContext();
  const { setActiveFieldFromHash } = questionnaireBloc;
  const { logicSteps, activeField, isSaving, isFetching } = questionnaireState;
  const location = useLocation();
  const firstStep = questionnaireBloc.findFirstFieldStep();

  const { fields, endScreens } = questionnaireState;

  useEffect(() => {
    if (!useRouting) {
      return;
    }
    const current = location.hash.replace("#", "");
    if (activeField && current !== activeField.ref) {
      if (activeField.ref !== firstStep?.ref) {
        window.location.hash = activeField.ref;
      }
    }
  }, [questionnaireState.activeField, useRouting]);

  useEffect(() => {
    let mounted = true;
    if (useRouting) {
      // update step after a timeout to not interfere with the auto-continue feature
      setTimeout(() => {
        if (mounted) {
          setActiveFieldFromHash(location.hash);
        }
      }, 100);
    }
    return () => {
      mounted = false;
    };
  }, [location.hash, useRouting]);

  useEffect(() => {
    if (onQuestionnaireData) {
      onQuestionnaireData(questionnaireState, questionnaireBloc);
    }
  }, [questionnaireState]);

  const shouldShowStep = useCallback(
    (field: QuestionnaireField) => {
      if (listAll) {
        return true;
      }
      if (formLike) {
        return logicSteps.includes(field.ref);
      }
      return false;
    },
    [logicSteps]
  );

  useEffect(() => {
    const currentStep = location.hash.replace("#", "");
    const currentIndex = logicSteps.indexOf(currentStep);
    onStep?.(currentIndex + 1, logicSteps.length);
  }, [onStep, fields, logicSteps, location.hash]);

  if (isFetching) {
    return (
      <ProgressIndicator indeterminate>
        <p className="as-subhead2 color-c-80">
          <Translate msg="loading" />
        </p>
      </ProgressIndicator>
    );
  }

  if (isSaving && !hideSavingUI) {
    return <QuestionnaireSavingState />;
  }

  return (
    <>
      {fields.length > 0 ? (
        <>
          {children}
          <WrapAllFields
            className={clsx({
              "list-all": listAll,
              "form-like": formLike
            })}
          >
            {fields.map((field) =>
              activeField?.id === field.id ? (
                <QuestionnaireStep
                  resetScrollOnEachStep={resetScrollOnEachStep}
                  key={field.id}
                  fields={[field]}
                  alwaysShow={shouldShowStep(field)}
                  validateOnInit={formLike}
                />
              ) : null
            )}
            {endScreens.map((field) =>
              formLike ? null : activeField?.id === field.id ? (
                <QuestionnaireStep
                  resetScrollOnEachStep={resetScrollOnEachStep}
                  key={field.id}
                  fields={[field]}
                  showThankYouStep={showThankYouStep}
                />
              ) : null
            )}
          </WrapAllFields>
        </>
      ) : (
        <QuestionnaireStateRenderer />
      )}
    </>
  );
};

const CustomQuestionnaire: FC<Props> = (props) => {
  const hiddenFields = QuestionnaireCubit.parseAnswers(props.answers);
  const questionnaireCubitId = props.instanceId
    ? `${props.id}:instance:${props.instanceId}`
    : props.id;

  const mergedFilterAnswerOptions = useMemo(() => {
    const releaseDateExclusions = getReleaseDateExclusions();
    return [...(props.filterAnswerOptions ?? []), ...releaseDateExclusions];
  }, [props.filterAnswerOptions]);

  useBlocNext(QuestionnaireCubit, {
    id: questionnaireCubitId,
    onMount: (bloc) => void bloc.initQuestionnaire(),
    props: {
      questionnaireId: props.id,
      forceRepeat: props.forceRepeat,
      onDataSent: props.onDataSent,
      onLastStepCompleted: props.onLastStepCompleted,
      onSavingChange: props.onSavingChange,
      onEndingScreenShown: props.onEndingScreenShown,
      disableAutoSubmit: props.disableAutoSubmit,
      disableTracking: props.disableTracking,
      preview: props.preview,
      instanceId: props.instanceId,
      preventLoadingUserAnswers: props.preventLoadingUserAnswers,
      hiddenFields: {
        ...hiddenFields,
        ...(props.hiddenFields ?? {})
      },
      answerOverrides: props.answerOverrides,
      filterAnswerOptions: mergedFilterAnswerOptions
    }
  });

  return (
    <QuestionnaireCubitIdContext.Provider value={questionnaireCubitId}>
      <QuestionnaireContent {...props} />
    </QuestionnaireCubitIdContext.Provider>
  );
};

export default CustomQuestionnaire;
